<template>
  <BaseView title="Fouling">
    <template #content>
      <v-container class="containerView screenBackground">
        <!-- TOP HALF -->
        <v-row
          class="rowView"
          style="height: 50%"
        >
          <v-col
            class="colView"
            :cols="6"
          >
            <FoulingRoutePanel
              :vessel-position="vesselPosition"
              :vessel-voyage="vesselVoyage"
              :vessel-start="vesselStart"
            />
          </v-col>

          <v-col
            class="colView"
            :cols="6"
          >
            <FoulingDetailsPanel
              ref="detailsPanel"
              :info-data="infoDetails && infoDetails['fouling_details_table']"
              :event-params="eventParams"
              :sel-event-data="selEventData"
              :det-headers="detHeaders"
              :det-items="detItems"
              :color-dict="colorDict"
              :data-table-height="dataTableHeight"
              @resize="updateDataTableHeight"
            />
          </v-col>
        </v-row>

        <!-- BOTTOM HALF -->
        <v-row
          class="rowView"
          style="height: 50%"
        >
          <v-col
            class="colView"
            :cols="6"
          >
            <FoulingChartPanel
              ref="foulingPanel"
              v-model="showFoulingDialog"
              :fouling-data="foulingData"
              :all-fouling="allFouling"
              :event-dict="eventDict"
            />
          </v-col>

          <v-col
            class="colView"
            :cols="6"
          >
            <OverconsumptionChartPanel
              ref="overconsPanel"
              v-model="showOverConsDialog"
              :cons-titles="consTitles"
              :cons-model="consModel"
              :cons-model-history="consModelHistory"
              :fouling-cons-data="foulingConsData"
              :all-overconsumption="allOverconsumption"
              :event-dict="eventDict"
              @cons-model-change="consModel = $event"
              @cons-model-history-change="consModelHistory = $event"
              @reflow="updateChartData"
            />
          </v-col>
        </v-row>

        <v-dialog v-model="alert">
          <v-alert
            type="error"
            transition="scale-transition"
            color="var(--color-error)"
            class="ma-0 body2"
          >
            No fouling data available for the selected period
          </v-alert>
        </v-dialog>
      </v-container>
    </template>

    <template #trailing>
      <ThemedSelector
        v-model="selFoulingEvent"
        :items="foulingEvents"
        solo
        flat
        hide-details
        color="primary"
        item-color="primary"
        item-text="name"
        item-value="id"
        style="max-width: 420px; font-size: 15px"
        class="my-auto mx-2"
      />
    </template>
  </BaseView>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
HighchartsMore(Highcharts);
import nodata from "highcharts/modules/no-data-to-display";
nodata(Highcharts);

import BaseView from "../components/BaseView.vue";
import ThemedSelector from "@/core/ThemedSelector.vue";

import FoulingRoutePanel from "./components/FoulingRoutePanel.vue";
import FoulingDetailsPanel from "./components/FoulingDetailsPanel.vue";
import FoulingChartPanel from "./components/FoulingChartPanel.vue";
import OverconsumptionChartPanel from "./components/OverconsumptionChartPanel.vue";

export default {
  name: "FoulingEvents",
  components: {
    BaseView,
    ThemedSelector,
    FoulingRoutePanel,
    FoulingDetailsPanel,
    FoulingChartPanel,
    OverconsumptionChartPanel,
  },

  data() {
    return {
      consModel: 0,
      consModelHistory: 0,
      consTitles: ["Cumulative Overconsumption", "Daily Overconsumption"],
      yTitles: [
        "Cumulative Overconsumption [tn]",
        "Daily Overconsumption [tn/day]",
      ],

      colorDict: {
        "No Data": "#757575",
        None: "#0AB10A",
        Light: "#FDDB36",
        Medium: "#FDBB36",
        Heavy: "#DD1C0D",
      },

      detHeaders: [
        { text: "", value: "part", class: "tableHeader bodyText" },
        {
          text: "Slime",
          value: "Slime",
          class: "tableHeader bodyText",
          align: "center",
          sortable: false,
        },
        {
          text: "Shells, animals, etc.",
          value: "Shells_animals_etc",
          class: "tableHeader bodyText",
          align: "center",
          sortable: false,
        },
        {
          text: "Growth",
          value: "Growth",
          class: "tableHeader bodyText",
          align: "center",
          sortable: false,
        },
        {
          text: "Damages",
          value: "Damage_to_blades",
          class: "tableHeader bodyText",
          align: "center",
          sortable: false,
        },
      ],

      detItems: [],
      beforeUrl: false,

      foulingData: {},
      foulingConsData: {},
      allFouling: {},
      allOverconsumption: {},

      eventParams: ["type", "location", "date", "provider"],
      eventDict: [
        { abbr: "DD", full: "Drydock" },
        { abbr: "PP", full: "Propeller cleaning" },
        { abbr: "HC", full: "Hull cleaning" },
        { abbr: "HC&PP", full: "Hull & propeller cleaning" },
        { abbr: "NB", full: "New Building" },
      ],

      selEventData: {
        type: {
          title: "Event Type",
          Value: "-",
          Label: "",
          format: 0,
          unit: "",
          error: false,
          errorMsg: "",
        },
        location: {
          title: "Location",
          Value: "-",
          Label: "",
          format: 0,
          unit: "",
          error: false,
          errorMsg: "",
        },
        date: {
          title: "Date",
          Value: "-",
          Label: "",
          format: 0,
          unit: "",
          error: false,
          errorMsg: "",
        },
        provider: {
          title: "Service Provider",
          Value: "-",
          Label: "",
          format: 0,
          unit: "",
          error: false,
          errorMsg: "",
        },
      },
      vesselPosition: [],
      vesselVoyage: [],
      vesselStart: [],

      selData: {},

      showFoulingDialog: false,
      showOverConsDialog: false,
      alert: false,
      dataTableHeight: 100,

      selFoulingEvent: null,
      foulingEvents: [],
    };
  },

  computed: {
    ...mapState("main", ["infoDetails"]),

    ...mapGetters([
      "getVesselFouling",
      "getVesselFoulingCons",
      "getFoulingLocations",
      "getBasicUrl",
      "getVesselsData",
      "getCurrentEvent",
    ]),

    ...mapGetters("main", ["getIdlePeriods"]),
  },

  watch: {

    getBasicUrl: {
      handler() {

        if (!this.beforeUrl) return;

        this.selEventData = {
          type: {
            title: "Event Type",
            Value: "-",
            Label: "",
            format: 0,
            unit: "",
            error: false,
            errorMsg: "",
          },
          location: {
            title: "Location",
            Value: "-",
            Label: "",
            format: 0,
            unit: "",
            error: false,
            errorMsg: "",
          },
          date: {
            title: "Date",
            Value: "-",
            Label: "",
            format: 0,
            unit: "",
            error: false,
            errorMsg: "",
          },
          provider: {
            title: "Service Provider",
            Value: "-",
            Label: "",
            format: 0,
            unit: "",
            error: false,
            errorMsg: "",
          },
        };
        this.vesselPosition = [];
        this.vesselVoyage = [];
        this.vesselStart = [];

        this.fetchVesselFouling(this.$route.params.vessel);
        this.fetchVesselFoulingCons(this.$route.params.vessel);
      },
      deep: true,
      immediate: true,
    },

    getVesselsData: {
      handler() {

        if (!this.getVesselsData) return;
        if (!this.selData || !this.selData.Date) return;
        if (!this.getVesselFouling || !this.getVesselFouling.log) return;

        let evIndex = this.getVesselFouling.log.indexOf(this.selData);
        if (evIndex < 0 && this.selData.id != null) {
          for (let i = 0; i < this.getVesselFouling.log.length; i++) {
            if (this.getVesselFouling.log[i].id == this.selData.id) {
              evIndex = i;
              break;
            }
          }
        }
        if (evIndex < 0) return;

        let nextEvDate;
        if (this.getVesselFouling.log.length > evIndex + 1)
          nextEvDate = this.getVesselFouling.log[evIndex + 1].Date;
        else nextEvDate = new Date().toISOString();

        if (!Array.isArray(this.getVesselsData)) return;

        const vess = this.getVesselsData.find((v) => v.idVessel == this.selData.vesselID);

        if (!vess) return;

        const postData = {
          vesselId: this.selData.vesselID,
          startdate: this.selData.Date,
          stopdate: nextEvDate,
          fastDate: vess.fastDate,
        };

        this.fetchFoulingLocations({
          vessel: this.$route.params.vessel,
          postData: postData,
        });
      },
      deep: true,
      immediate: true,
    },

    "$route.params": {
      handler() {

        if (this.getBasicUrl == "") {
          this.beforeUrl = true;
          return;
        }

        this.selEventData = {
          type: {
            title: "Event Type",
            Value: "-",
            Label: "",
            format: 0,
            unit: "",
            error: false,
            errorMsg: "",
          },
          location: {
            title: "Location",
            Value: "-",
            Label: "",
            format: 0,
            unit: "",
            error: false,
            errorMsg: "",
          },
          date: {
            title: "Date",
            Value: "-",
            Label: "",
            format: 0,
            unit: "",
            error: false,
            errorMsg: "",
          },
          provider: {
            title: "Service Provider",
            Value: "-",
            Label: "",
            format: 0,
            unit: "",
            error: false,
            errorMsg: "",
          },
        };
        this.vesselPosition = [];
        this.vesselVoyage = [];
        this.vesselStart = [];

        this.fetchVesselFouling(this.$route.params.vessel);
        this.fetchVesselFoulingCons(this.$route.params.vessel);
      },
      deep: true,
      immediate: true,
    },

    consModel: {
      handler() {

        if (!this.foulingConsData || !this.foulingConsData.series) return;

        for (let i = 0; i < this.foulingConsData.series.length; i++) {
          this.foulingConsData.series[i].visible = false;
        }

        if (this.foulingConsData.series[this.consModel]) {
          this.foulingConsData.series[this.consModel].visible = true;
        }

        if (this.foulingConsData.yAxis && this.foulingConsData.yAxis.title) {
          this.foulingConsData.yAxis.title.text = this.yTitles[this.consModel];
        }

        if (this.foulingConsData.yAxis) {
          this.foulingConsData.yAxis.max = null;
        }

      },
      deep: true,
      immediate: true,
    },

    consModelHistory: {
      handler() {

        if (!this.allOverconsumption || !this.allOverconsumption.series) return;

        for (let i = 0; i < this.allOverconsumption.series.length; i++) {
          this.allOverconsumption.series[i].visible = false;
        }

        for (let j = 0; j < this.allOverconsumption.series.length; j++) {
          if (j % 2 == this.consModelHistory)
            this.allOverconsumption.series[j].visible = true;
        }

        if (this.allOverconsumption.yAxis && this.allOverconsumption.yAxis.title) {
          this.allOverconsumption.yAxis.title.text =
            this.yTitles[this.consModelHistory];
        }

      },
      deep: true,
      immediate: true,
    },

    getCurrentEvent: {
      handler() {

        if (!this.getVesselFouling || !this.getVesselFouling.log) return;
        if (this.getCurrentEvent == null) return;
        if (typeof this.getCurrentEvent === "object") return;

        const found = this.getVesselFouling.log.find((item) => item.id == this.getCurrentEvent);

        if (found) this.selData = found;
      },
      deep: true,
      immediate: true,
    },

    getFoulingLocations: {
      handler() {

        if (!this.getFoulingLocations) return;
        if (!this.getFoulingLocations.coords) return;
        if (!Array.isArray(this.getFoulingLocations.coords)) return;
        if (this.getFoulingLocations.coords.length === 0) return;

        this.vesselPosition = [];
        this.vesselVoyage = [];
        this.vesselStart = [];

        const colorNum = 5;

        const colorRange = this.createColorRange(
          this.hexToRgb("#B6F4F6"),
          this.hexToRgb("#B3F499"),
          colorNum - 1
        );

        let step = Math.floor(this.getFoulingLocations.coords.length / colorNum);
        if (step <= 0) step = 1;

        for (let i = 0; i < this.getFoulingLocations.coords.length; i++) {
          const item = this.getFoulingLocations.coords[i];
          const normalizedCoords = this.normalizeMapCoords(item.coords);

          if (!normalizedCoords) {
            continue;
          }

          this.vesselVoyage.push({
            color: colorRange[Math.floor(i / step)] || colorRange[colorRange.length - 1],
            coords: normalizedCoords,
          });
        }

        const lastCoords = this.normalizeMapCoords(
          this.getFoulingLocations.coords[
            this.getFoulingLocations.coords.length - 1
          ].coords
        );

        if (lastCoords) {
          this.vesselPosition.push({
            Vessel: this.$route.params.vessel,
            coordinates: lastCoords,
            color: "#134F91",
          });
        }

        const firstCoords = this.normalizeMapCoords(
          this.getFoulingLocations.coords[0].coords
        );

        if (firstCoords) {
          this.vesselStart.push({
            Vessel: this.$route.params.vessel,
            coordinates: firstCoords,
            color: "#9FC1E7",
          });
        }

        for (let k = 1; k < this.vesselVoyage.length - 1; k++) {
          const start = this.vesselVoyage[k].coords;
          const stop = this.vesselVoyage[k + 1].coords;

          if (
            !Array.isArray(start) ||
    !Array.isArray(stop) ||
    start.length < 2 ||
    stop.length < 2
          ) {
            continue;
          }

          const brg = this.angle(start[1], start[0], stop[1], stop[0]);

          if (!isFinite(brg)) {
            continue;
          }

          if (brg > 337.5 || brg < 22.5) this.vesselVoyage[k].dir = "top";
          else if (brg < 67.5) this.vesselVoyage[k].dir = "top-right";
          else if (brg < 112.5) this.vesselVoyage[k].dir = "right";
          else if (brg < 157.5) this.vesselVoyage[k].dir = "bottom-right";
          else if (brg < 202.5) this.vesselVoyage[k].dir = "bottom";
          else if (brg < 247.5) this.vesselVoyage[k].dir = "bottom-left";
          else if (brg < 292.5) this.vesselVoyage[k].dir = "left";
          else if (brg < 337.5) this.vesselVoyage[k].dir = "top-left";
        }

      },
      deep: true,
      immediate: true,
    },

    getIdlePeriods: {
      handler() {
        if (!this.selData || !this.selData.Date) return;
        if (!this.getVesselFouling || !this.getVesselFouling.log) return;

        let evIndex = this.getVesselFouling.log.indexOf(this.selData);
        if (evIndex < 0 && this.selData.id != null) {
          for (let i = 0; i < this.getVesselFouling.log.length; i++) {
            if (this.getVesselFouling.log[i].id == this.selData.id) {
              evIndex = i;
              break;
            }
          }
        }
        if (evIndex < 0) return;

        let nextEvDate;
        if (this.getVesselFouling.log.length > evIndex + 1)
          nextEvDate = this.getVesselFouling.log[evIndex + 1].Date;
        else nextEvDate = new Date().toISOString();

        this.setChartData(this.selData.Date, this.selData.Entry_type, nextEvDate);
      },
      deep: true,
      immediate: true,
    },

    selData() {

      if (!this.selData || !this.selData.Date) return;
      if (!this.getVesselFouling || !this.getVesselFouling.log) return;

      this.vesselPosition = [];
      this.vesselVoyage = [];
      this.vesselStart = [];

      let evIndex = this.getVesselFouling.log.indexOf(this.selData);
      if (evIndex < 0 && this.selData.id != null) {
        for (let i = 0; i < this.getVesselFouling.log.length; i++) {
          if (this.getVesselFouling.log[i].id == this.selData.id) {
            evIndex = i;
            break;
          }
        }
      }
      if (evIndex < 0) return;

      let nextEvDate;
      if (this.getVesselFouling.log.length > evIndex + 1)
        nextEvDate = this.getVesselFouling.log[evIndex + 1].Date;
      else nextEvDate = new Date().toISOString();

      this.fetchIdlePeriods({
        vesselId: this.selData.vesselID,
        startdate: this.selData.Date,
        stopdate: nextEvDate,
      });

      // Details table
      this.detItems = [];
      const params = ["Slime", "Shells_animals_etc", "Growth", "Damage_to_blades"];
      const parts = ["Boottop", "VS", "FB", "PR"];

      for (let p = 0; p < parts.length; p++) {
        const part = parts[p];
        const row = { part: part };
        for (let q = 0; q < params.length; q++) {
          const param = params[q];
          row[param] = this.selData[part + "_" + param];
        }
        this.detItems.push(row);
      }

      // Cards
      this.selEventData = {
        type: {
          title: "Event Type",
          Value: "-",
          Label: "",
          format: 0,
          unit: "",
          error: false,
          errorMsg: "",
        },
        location: {
          title: "Location",
          Value: "-",
          Label: "",
          format: 0,
          unit: "",
          error: false,
          errorMsg: "",
        },
        date: {
          title: "Date",
          Value: "-",
          Label: "",
          format: 0,
          unit: "",
          error: false,
          errorMsg: "",
        },
        provider: {
          title: "Service Provider",
          Value: "-",
          Label: "",
          format: 0,
          unit: "",
          error: false,
          errorMsg: "",
        },
      };
      this.selEventData["location"] = {
        title: "Location",
        Value: this.selData.Location,
        Label: "",
        format: 0,
        unit: "",
        error: false,
        errorMsg: "",
      };
      this.selEventData["provider"] = {
        title: "Service Provider",
        Value: this.selData.Service_provider,
        Label: "",
        format: 0,
        unit: "",
        error: false,
        errorMsg: "",
      };
      this.selEventData["date"] = {
        title: "Date",
        Value: this.selData.Date.substring(0, 10),
        Label: "",
        format: 0,
        unit: "",
        error: false,
        errorMsg: "",
      };
      this.selEventData["type"] = {
        title: "Event Type",
        Value: this.selData.Entry_type,
        Label: "",
        format: 0,
        unit: "",
        error: false,
        errorMsg: "",
      };

      // Fetch voyage locations
      if (Array.isArray(this.getVesselsData)) {
        const vess = this.getVesselsData.find((v) => v.idVessel == this.selData.vesselID);

        if (vess) {
          const postData = {
            vesselId: this.selData.vesselID,
            startdate: this.selData.Date,
            stopdate: nextEvDate,
            fastDate: vess.fastDate,
          };

          this.fetchFoulingLocations({
            vessel: this.$route.params.vessel,
            postData: postData,
          });
        }
      }

      document.body.style.cursor = "default";
    },

    showFoulingDialog: {
      handler() {

        if (this.showFoulingDialog) {
          this.setFoulingHistoryChart();
          const self = this;
          setTimeout(function () {

            if (
              self.$refs &&
              self.$refs.foulingPanel &&
              self.$refs.foulingPanel.$refs &&
              self.$refs.foulingPanel.$refs.allFoulChart &&
              self.$refs.foulingPanel.$refs.allFoulChart.chart &&
              self.$refs.foulingPanel.$refs.allFoulChart.chart.updateContainerScaling
            ) {
              self.$refs.foulingPanel.$refs.allFoulChart.chart.updateContainerScaling();
            }
          }, 500);
        }
      },
      immediate: true,
    },

    showOverConsDialog: {
      handler() {

        if (this.showOverConsDialog) {
          this.setOverconsumptionHistoryChart();
          const self = this;
          setTimeout(function () {

            if (
              self.$refs &&
              self.$refs.overconsPanel &&
              self.$refs.overconsPanel.$refs &&
              self.$refs.overconsPanel.$refs.allOverConsChart &&
              self.$refs.overconsPanel.$refs.allOverConsChart.chart &&
              self.$refs.overconsPanel.$refs.allOverConsChart.chart.updateContainerScaling
            ) {
              self.$refs.overconsPanel.$refs.allOverConsChart.chart.updateContainerScaling();
            }
          }, 500);
        }
      },
      immediate: true,
    },

    getVesselFouling: {
      handler() {
        this.selFoulingEvent = null;
        this.foulingEvents = [];

        if (!this.getVesselFouling || !this.getVesselFouling.log) {
          return;
        }

        this.foulingEvents = this.getVesselFouling.log.map((item) => {
          return {
            name: item.Entry_type + " at " + item.Date.substring(0, 10),
            id: item.id,
            date: item.Date.substring(0, 10),
          };
        });

        this.foulingEvents.sort(function (a, b) {
          return a.date > b.date ? -1 : 1;
        });

        if (this.foulingEvents.length > 0)
          this.selFoulingEvent = this.foulingEvents[0].id;

      },
      deep: true,
      immediate: true,
    },

    getVesselFoulingCons: {
      handler() {

        if (!this.getVesselFoulingCons || this.getVesselFoulingCons.length === 0) return;
        this.setMainOverconsumptionChart();
      },
      deep: true,
      immediate: true,
    },

    selFoulingEvent: {
      handler(newVal) {

        if (newVal == null) return;

        let id = newVal;
        if (typeof id === "string") id = parseInt(id, 10);

        if (this.getVesselFouling && this.getVesselFouling.log) {
          const found = this.getVesselFouling.log.find(function (item) {
            return item && item.id == id;
          });

          if (found) {
            this.selData = found;

          }
        }

        this.$store.commit("setCurrentEvent",id);
      },
      deep: false,
      immediate: true,
    },
  },

  methods: {
    ...mapActions([
      "fetchVesselFouling",
      "fetchVesselFoulingCons",
      "fetchFoulingLocations",
    ]),

    ...mapActions("main", ["fetchIdlePeriods"]),

    // reflow main overcons chart (now inside child component)
    updateChartData() {
      if (
        this.$refs &&
        this.$refs.overconsPanel &&
        this.$refs.overconsPanel.$refs &&
        this.$refs.overconsPanel.$refs.foulingConsChart &&
        this.$refs.overconsPanel.$refs.foulingConsChart.chart
      ) {
        this.$refs.overconsPanel.$refs.foulingConsChart.chart.reflow();
      }
    },

    // table height (now inside DetailsPanel)
    updateDataTableHeight() {
      if (
        this.$refs &&
        this.$refs.detailsPanel &&
        this.$refs.detailsPanel.$refs &&
        this.$refs.detailsPanel.$refs.parentDiv &&
        this.$refs.detailsPanel.$refs.parentDiv.$el &&
        this.$refs.detailsPanel.$refs.parentDiv.$el.clientHeight
      ) {
        this.dataTableHeight =
          this.$refs.detailsPanel.$refs.parentDiv.$el.clientHeight;

      }
    },

    setMainOverconsumptionChart() {

      if (!this.selData || !this.selData.Date) {
        return;
      }
      if (!this.getVesselFouling || !this.getVesselFouling.log) {
        return;
      }

      let evIndex = this.getVesselFouling.log.indexOf(this.selData);

      if (evIndex < 0 && this.selData.id != null) {
        for (let i = 0; i < this.getVesselFouling.log.length; i++) {
          if (this.getVesselFouling.log[i].id == this.selData.id) {
            evIndex = i;
            break;
          }
        }
      }

      if (evIndex < 0) {
        return;
      }

      let nextEvDate;
      if (this.getVesselFouling.log.length > evIndex + 1)
        nextEvDate = this.getVesselFouling.log[evIndex + 1].Date;
      else nextEvDate = new Date().toISOString();

      this.setChartData(this.selData.Date, this.selData.Entry_type, nextEvDate);
    },

    setChartData(evDate, evType, nextEvDate) {

      if (!this.getVesselFouling || !this.getVesselFouling.polynomials) {
        return;
      }
      if (!this.getVesselFoulingCons || !Array.isArray(this.getVesselFoulingCons)) {
        return;
      }

      this.alert = false;

      this.foulingData = {
        credits: { enabled: false },
        time: { useUTC: true },
        title: { text: null },
        tooltip: {
          formatter: function () {
            return this.points.reduce(function (s, point) {
              return s + "<br/>" + point.series.name + ": " + point.y.toFixed(2);
            }, "<b>" + new Date(this.x).toISOString().slice(0, 10) + "</b>");
          },
          shared: true,
        },
        yAxis: {
          min: -12,
          max: 12,
          gridLineWidth: 1,
          title: { text: "Power [%]", align: "high" },
          plotLines: [
            { value: 0, color: "#9CA3AF", dashStyle: "dash", width: 1.5 },
          ],
        },
        xAxis: {
          type: "datetime",
          labels: { format: "{value:%e %b %Y}" },
          tickInterval: 24 * 3600 * 1000 * 30,
          crosshair: true,
          plotLines: [
            {
              color: "#DC2626",
              dashStyle: "dash",
              value: new Date(evDate).getTime(),
              width: 2,
              label: {
                rotation: 0,
                text: evType,
                align: "left",
                x: 10,
                y: 20,
                style: { color: "var(--color-text)" },
              },
              zIndex: 5,
            },
          ],
        },
        exporting: { enabled: false },
        legend: { enabled: true, align: "right", verticalAlign: "top", floating: true },
        series: [],
      };

      this.foulingConsData = {
        credits: { enabled: false },
        time: { useUTC: true },
        title: { text: null },
        tooltip: {
          formatter: function () {
            return this.points.reduce(function (s, point) {
              return s + "<br/>" + point.series.name + ": " + point.y.toFixed(2);
            }, "<b>" + new Date(this.x).toISOString().slice(0, 10) + "</b>");
          },
          shared: true,
        },
        yAxis: {
          min: null,
          max: null,
          gridLineWidth: 1,
          title: { align: "high" },
        },
        xAxis: {
          type: "datetime",
          labels: { format: "{value:%e %b %Y}" },
          crosshair: true,
          tickInterval: 24 * 3600 * 1000 * 30,
          plotLines: [
            { color: "#DC2626", dashStyle: "dash", value: new Date(evDate).getTime(), width: 2 },
          ],
        },
        exporting: { enabled: false },
        legend: { enabled: false },
        series: [
          {
            name: "Cumulative Consumption",
            color: "#2563EB",
            type: "spline",
            data: [],
            marker: { enabled: false },
            visible: false,
          },
          {
            name: "Daily Consumption",
            color: "#2563EB",
            type: "spline",
            data: [],
            marker: { enabled: false },
            visible: false,
          },
        ],
      };

      const anyPoints = this.getVesselFouling.polynomials.find(function (item) {
        if (!item || !item.date) return false;
        return (
          item.date.substring(0, 10) >= evDate.substring(0, 10) &&
          item.date.substring(0, 10) <= nextEvDate.substring(0, 10)
        );
      });

      if (anyPoints == undefined) {
        this.alert = true;
        return;
      }

      const periodID = anyPoints.period;

      const periodData = this.getVesselFouling.polynomials.filter(function (item) {
        return item.period == periodID;
      });

      const pointData = (this.getVesselFouling["mean_change_adj"] || []).filter(
        function (item) {
          return item.period == periodID;
        }
      );

      const consData = this.getVesselFoulingCons.find(function (item) {
        return item.period == periodID;
      });

      if (!consData || !consData.date) {
        console.warn(
          "[setChartData] No overconsumption data for periodID, but fouling chart will continue:",
          periodID
        );
      } else {
        for (let i = 0; i < (consData.cons || []).length; i++) {
          this.foulingConsData.series[0].data.push([
            new Date(consData.date[i]).getTime(),
            Number(Number(consData.cons[i]).toFixed(2)),
          ]);
        }

        for (let j = 0; j < (consData.overconsumption || []).length; j++) {
          this.foulingConsData.series[1].data.push([
            new Date(consData.date[j]).getTime(),
            Number(Number(consData.overconsumption[j]).toFixed(2)),
          ]);
        }

        this.foulingConsData.series[this.consModel].visible = true;
        this.foulingConsData.yAxis.title.text = this.yTitles[this.consModel];
      }

      const pred = {
        name: "Prediction",
        type: "spline",
        color: "#4169B4",
        data: [],
        marker: { enabled: false },
      };

      const predLinear = {
        name: "Linear prediction",
        type: "spline",
        color: "#A2142F",
        lineWidth: 4,
        data: [],
        marker: { enabled: false },
      };

      const predPoints = {
        name: "Data points",
        type: "scatter",
        color: "#5D5D5D",
        data: [],
        marker: { enabled: true, symbol: "circle", radius: 3 },
      };

      periodData.forEach(function (day) {
        const xValue = new Date(day.date).getTime();
        if (day.preds !== null && day.preds !== undefined) {
          pred.data.push([xValue, Number(Number(day.preds).toFixed(2))]);
          predLinear.data.push([xValue, Number(Number(day.preds_linear).toFixed(2))]);
        }
      });

      pointData.forEach(function (day) {
        const xValue = new Date(day.refTime).getTime();
        predPoints.data.push([xValue, day["mean_change_adj"]]);
      });

      this.foulingData.series.push(predPoints);
      this.foulingData.series.push(predLinear);
      this.foulingData.series.push(pred);

      if (this.getIdlePeriods && this.getIdlePeriods.length > 0) {

        for (let k = 0; k < this.getIdlePeriods.length; k++) {
          const period = this.getIdlePeriods[k];
          const start = new Date(period.start).getTime();
          const end = new Date(period.end).getTime();

          if (!isFinite(start) || !isFinite(end)) {
            continue;
          }

          this.foulingData.series.push({
            name: k === 0 ? "Idle Period" : "",
            type: "arearange",
            linkedTo: k === 0 ? undefined : ":previous",
            data: [
              [start, -15, 15],
              [end, -15, 15],
            ],
            color: "rgba(42, 74, 58, 0.25)",
            lineWidth: 1,
            lineColor: "rgba(80, 140, 100, 0.6)",
            enableMouseTracking: false,
            marker: { enabled: false },
            zIndex: 0,
            showInLegend: k === 0,
          });
        }
      }

    },

    createColorRange(c1, c2, num) {

      const colorList = [];
      if (!c1 || !c2 || !num || num <= 0) return colorList;

      let step = Math.floor(255 / num);
      if (step <= 0) step = 1;

      for (let i = 0; i < 255; i += step) {
        const r = Math.floor(c1[1] + (i * (c2[1] - c1[1])) / 255);
        const g = Math.floor(c1[2] + (i * (c2[2] - c1[2])) / 255);
        const b = Math.floor(c1[3] + (i * (c2[3] - c1[3])) / 255);
        colorList.push("rgb(" + [r, g, b].join(",") + ")");
      }
      return colorList;
    },

    hexToRgb(hex) {
      const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
      if (!m) return [0, 0, 0, 0];
      return [0, parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
    },

    convertCoords(input) {

      if (typeof input === "number") return input;
      if (typeof input !== "string") return NaN;

      const parts = input.match(/\d+/g);
      const dirMatch = input.match(/[NSEW]/i);
      if (!parts || parts.length < 2 || !dirMatch) return NaN;

      const nums = parts.map(Number);
      const dir = dirMatch[0].toUpperCase();

      const deg = nums[0] || 0;
      const min = nums[1] || 0;
      const sec = nums[2] || 0;

      let coord = deg + min / 60 + sec / 3600;
      if (dir === "S" || dir === "W") coord = coord * -1;
      return coord;
    },
    normalizeMapCoords(coords) {
      if (!Array.isArray(coords) || coords.length < 2) {
        return null;
      }

      const first = this.convertCoords(coords[0]);
      const second = this.convertCoords(coords[1]);

      if (!isFinite(first) || !isFinite(second)) {
        return null;
      }

      return [first, second];
    },

    radians(n) {
      return n * (Math.PI / 180);
    },

    degrees(n) {
      return n * (180 / Math.PI);
    },

    setFoulingHistoryChart() {
      if (!this.getVesselFouling || !this.getVesselFouling.polynomials) {
        return;
      }

      this.allFouling = {
        credits: { enabled: false },
        time: { useUTC: true },
        chart: {},
        title: { text: null },
        tooltip: {
          formatter: function () {
            return this.points.reduce(function (s, point) {
              return s + "<br/>" + point.series.name + ": " + point.y.toFixed(2);
            }, "<b>" + new Date(this.x).toISOString().slice(0, 10) + "</b>");
          },
          shared: true,
        },
        yAxis: {
          gridLineWidth: 2,
          title: { text: "Power [%]", align: "high" },
          plotLines: [
            { value: 0, color: "var(--color-text)", dashStyle: "dash", width: 1.5 },
          ],
        },
        xAxis: {
          type: "datetime",
          labels: { format: "{value:%e %b %Y}" },
          tickInterval: 24 * 3600 * 1000 * 60,
          crosshair: true,
          plotLines: [],
        },
        exporting: { enabled: false },
        legend: { enabled: false },
        series: [],
      };

      const predPoints = {
        name: "",
        type: "scatter",
        color: "var(--grid-line-color)",
        data: [],
        marker: { enabled: true, symbol: "circle", radius: 3 },
        animation: false,
        states: {
          hover: { enabled: false },
          normal: { animation: false },
          inactive: { opacity: 1 },
        },
      };

      const perID = [
        ...new Set(this.getVesselFouling.polynomials.map((item) => item.period)),
      ].sort(function (a, b) {
        return a - b;
      });

      this.allFouling.series.push(predPoints);

      let maxX = null;
      let minX = null;

      // Loop through periods to add prediction data
      for (let p = 0; p < perID.length; p++) {
        const period = perID[p];

        const pred = {
          name: "Prediction",
          colorIndex: 3,
          type: "spline",
          data: [],
          marker: { enabled: false },
          animation: false,
          states: {
            hover: { enabled: false },
            normal: { animation: false },
            inactive: { opacity: 1 },
          },
        };

        const predLinear = {
          name: "Linear prediction",
          type: "spline",
          color: "#F75C00",
          lineWidth: 5,
          data: [],
          marker: { enabled: false },
          animation: false,
          states: {
            hover: { enabled: false },
            normal: { animation: false },
            inactive: { opacity: 1 },
          },
        };

        const periodData = this.getVesselFouling.polynomials.filter(function (item) {
          return item.period == period;
        });

        const pointData = (this.getVesselFouling["mean_change_adj"] || []).filter(
          function (item) {
            return item.period == period;
          }
        );

        periodData.forEach(function (day) {
          const xValue = new Date(day.date).getTime();
          if (day.preds !== null && day.preds !== undefined) {
            pred.data.push([xValue, Number(Number(day.preds).toFixed(2))]);
            predLinear.data.push([xValue, Number(Number(day.preds_linear).toFixed(2))]);
            if (minX === null) minX = xValue;
            maxX = xValue;
          }
        });

        pointData.forEach(function (day) {
          const xValue = new Date(day.refTime).getTime();
          predPoints.data.push([xValue, day["mean_change_adj"]]);
          if (minX === null) minX = xValue;
          maxX = xValue;
        });

        this.allFouling.series.push(predLinear);
        this.allFouling.series.push(pred);
      }

      // Add event plot lines
      if (this.getVesselFouling.log && this.getVesselFouling.log.length > 0) {

        for (let i = 0; i < this.getVesselFouling.log.length; i++) {
          const event = this.getVesselFouling.log[i];
          const abbr = this.eventDict.find(function (it) {
            return event.Entry_type &&
          event.Entry_type.toLowerCase().startsWith(it.full.toLowerCase());
          });

          if (abbr) {
            this.allFouling.xAxis.plotLines.push({
              color: "#DD1C0D",
              dashStyle: "dash",
              value: new Date(event.Date).getTime(),
              width: 2,
              label: {
                rotation: 0,
                text: abbr.abbr,
                align: "left",
                x: 10,
                y: 20,
                style: { color: "var(--color-text)" },
              },
              zIndex: 1,
            });
          }
        }
      }

      // Add idle periods to history chart
      if (this.getIdlePeriods && this.getIdlePeriods.length > 0) {

        for (let k = 0; k < this.getIdlePeriods.length; k++) {
          const period = this.getIdlePeriods[k];
          const start = new Date(period.start).getTime();
          const end = new Date(period.end).getTime();

          if (!isFinite(start) || !isFinite(end)) {
            continue;
          }

          this.allFouling.series.push({
            name: k === 0 ? "Idle Period" : "",
            type: "arearange",
            linkedTo: k === 0 ? undefined : ":previous",
            data: [
              [start, -15, 15],
              [end, -15, 15],
            ],
            color: "rgba(42, 74, 58, 0.25)",
            lineWidth: 1,
            lineColor: "rgba(80, 140, 100, 0.6)",
            enableMouseTracking: false,
            marker: { enabled: false },
            zIndex: 0,
            showInLegend: k === 0,
          });
        }
      }

      if (minX === null || maxX === null || predPoints.data.length === 0) {
        return;
      }

      const ys = predPoints.data.map(function (o) {
        return o[1];
      });

      let maxY = Math.ceil(Math.max.apply(Math, ys));
      let minY = Math.floor(Math.min.apply(Math, ys));

      if (maxY < 10) maxY = 10;
      if (minY > -10) minY = -10;

      const axisLimits = {
        name: "",
        type: "scatter",
        color: "transparent",
        data: [
          [minX, minY],
          [maxX + (maxX - minX) * 0.1, maxY],
        ],
        marker: { enabled: true, symbol: "circle", radius: 3 },
        animation: false,
        states: {
          hover: { enabled: false },
          normal: { animation: false },
          inactive: { opacity: 1 },
        },
      };

      this.allFouling.series.push(axisLimits);

    },

    setOverconsumptionHistoryChart() {

      if (!this.getVesselFoulingCons || !Array.isArray(this.getVesselFoulingCons)){
        return;
      }

      this.consModelHistory = 0;

      this.allOverconsumption = {
        credits: { enabled: false },
        time: { useUTC: true },
        title: { text: null },
        chart: {},
        tooltip: {
          //formatter: function () {
          //  return this.points.reduce(function (s, point) {
          //    return s + "<br/>" + point.series.name + ": " + point.y.toFixed(2);
          //  }, "<b>" + new Date(this.x).toISOString().slice(0, 10) + "</b>");
          //},
          shared: true,
        },
        yAxis: {
          min: null,
          max: null,
          gridLineWidth: 2,
          title: { align: "high", text: "Cumulative Overconsumption [tn]" },
        },
        xAxis: {
          min: null,
          max: null,
          type: "datetime",
          labels: { format: "{value:%e %b %Y}" },
          crosshair: true,
          plotLines: [],
          tickInterval: 24 * 3600 * 1000 * 30,
        },
        exporting: { enabled: false },
        legend: { enabled: false },
        series: [],
      };

      const historyConsData = this.getVesselFoulingCons
        .filter(function (item) {
          return item && item.date && Array.isArray(item.date);
        })
        .sort(function (a, b) {
          const aTime = new Date(a.date && a.date[0]).getTime();
          const bTime = new Date(b.date && b.date[0]).getTime();
          return aTime - bTime;
        });

      for (let p = 0; p < historyConsData.length; p++) {
        const consData = historyConsData[p];

        const cumulative = {
          name: "Cumulative Consumption",
          color: "#4A7EBA",
          type: "spline",
          data: [],
          marker: { enabled: false },
          animation: false,
          states: {
            hover: { enabled: false },
            normal: { animation: false },
            inactive: { opacity: 1 },
          },
          visible: true,
        };

        const daily = {
          name: "Daily Consumption",
          color: "#4A7EBA",
          type: "spline",
          data: [],
          marker: { enabled: false },
          animation: false,
          states: {
            hover: { enabled: false },
            normal: { animation: false },
            inactive: { opacity: 1 },
          },
          visible: false,
        };

        for (let i = 0; i < (consData.cons || []).length; i++) {
          cumulative.data.push([new Date(consData.date[i]).getTime(), Number(Number(consData.cons[i]).toFixed(2))]);
        }

        for (let j = 0; j < (consData.overconsumption || []).length; j++) {
          daily.data.push([
            new Date(consData.date[j]).getTime(),
            Number(Number(consData.overconsumption[j]).toFixed(2)),
          ]);
        }

        this.allOverconsumption.series.push(cumulative);
        this.allOverconsumption.series.push(daily);
      }

      if (this.getVesselFouling.log && this.getVesselFouling.log.length > 0) {
        for (let e = 0; e < this.getVesselFouling.log.length; e++) {
          const event = this.getVesselFouling.log[e];

          const abbr = this.eventDict.find(function (it) {
            return event.Entry_type && event.Entry_type.startsWith(it.full);
          });

          if (abbr) {
            this.allOverconsumption.xAxis.plotLines.push({
              color: "#DD1C0D",
              dashStyle: "dash",
              value: new Date(event.Date).getTime(),
              width: 2,
              label: {
                rotation: 0,
                text: abbr.abbr,
                align: "left",
                x: 10,
                y: 20,
                style: { color: "var(--color-text)" },
              },
              zIndex: 1,
            });
          }
        }
      }

      if (this.allOverconsumption.xAxis.plotLines.length > 0) {
        this.allOverconsumption.xAxis.min =
          this.allOverconsumption.xAxis.plotLines[0].value;
      }

    },

    angle(startLat, startLong, endLat, endLong) {
      startLat = this.radians(startLat);
      startLong = this.radians(startLong);
      endLat = this.radians(endLat);
      endLong = this.radians(endLong);

      let dLong = endLong - startLong;

      const dPhi = Math.log(
        Math.tan(endLat / 2.0 + Math.PI / 4.0) /
          Math.tan(startLat / 2.0 + Math.PI / 4.0)
      );

      if (Math.abs(dLong) > Math.PI) {
        if (dLong > 0.0) dLong = -(2.0 * Math.PI - dLong);
        else dLong = 2.0 * Math.PI + dLong;
      }

      return (this.degrees(Math.atan2(dLong, dPhi)) + 360.0) % 360.0;
    },
  },

};
</script>
